import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { EMPTY_ARRAY } from '~/core/util/array';
import {
  getBookingWidgetFamilyBooking,
  getIsFamilyVisitActivatedInBookingWidget,
  selectFamilyBookingTotalPatients,
  setApplyIdToAllMembers,
  setApplyInsuranceToAllMembers,
  setAuxPatientInfo,
} from '~/ducks/bookingWidgetFamilyBooking';
import { useSolvSelector } from '../../reducers';

/**
 * Returns whether or not the user of the booking widget has indicated
 * they anticipate making an appointment for 2+ patients. Does NOT
 * indicate whether form field data has been entered for any auxiliary
 * patients.
 */
export const useBookingWidgetIsFamilyBooking = () => {
  return useSolvSelector(getIsFamilyVisitActivatedInBookingWidget);
};

export const useBookingWidgetFamilyBookingData = () => {
  const dispatch = useDispatch();
  const familyBooking = useSolvSelector(getBookingWidgetFamilyBooking);
  const anticipatedFamilySize = familyBooking?.numberOfAuxPatients ?? 0;
  const auxPatients = familyBooking?.auxPatients ?? EMPTY_ARRAY;
  const shouldApplyIDToAll = familyBooking?.applyIDToAllFamilyMembers ?? false;
  const setShouldApplyIDToAll = useCallback(
    (should: boolean) => {
      dispatch(setApplyIdToAllMembers(should));
    },
    [dispatch]
  );
  const shouldApplyInsuranceToAll = familyBooking?.applyInsuranceToAllFamilyMembers ?? false;
  const setShouldApplyInsuranceToAll = useCallback(
    (should: boolean) => {
      dispatch(setApplyInsuranceToAllMembers(should));
    },
    [dispatch]
  );

  const isFamilyBooking = familyBooking?.numberOfAuxPatients > 0;

  const setAnticipatedFamilySize = useCallback(
    (size: number) => {
      dispatch(selectFamilyBookingTotalPatients(size));
    },
    [dispatch]
  );

  const replaceAuxPatients = useCallback(
    (auxPatients: Parameters<typeof setAuxPatientInfo>[0]) => {
      dispatch(setAuxPatientInfo(auxPatients));
    },
    [dispatch]
  );

  return {
    auxPatients,
    replaceAuxPatients,
    shouldApplyIDToAll,
    setShouldApplyIDToAll,
    shouldApplyInsuranceToAll,
    setShouldApplyInsuranceToAll,
    isFamilyBooking,
    anticipatedFamilySize,
    setAnticipatedFamilySize,
  };
};
